import { Trans } from "@lingui/react/macro";
import { observable } from "mobx";
import { observer } from "mobx-react-lite";
import { useState } from "react";

import {
  type PatientMonitoringType,
  type PatientStatus,
  apiPostMonitoringSettingsMonitoring,
} from "../../../api";
import { Button } from "../../../components/ui-kit/button";
import {
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
} from "../../../components/ui-kit/dialog";
import { Form } from "../../../components/ui-kit/form/form";
import { assert } from "../../../utils/utils";
import { AdminConsole } from "../admin-console";
import { MonitoringSettingsForm } from "./monitoring-settings-form";

export const AddMonitoringSettingsDialog = observer(
  function AddMonitoringSettingsDialog({
    admin,
    onClose,
    onSaved,
  }: {
    admin: AdminConsole;
    onClose: () => void;
    onSaved: () => void;
  }) {
    const [fields] = useState(() =>
      observable({
        monitoring_type: null as PatientMonitoringType | null,
        country: null as string | null,
        region: null as number | null,
        new_patient_status: null as PatientStatus | null,
        auto_schedule: false,
        requires_measurement_config: false,
        auto_alerts: false,
        consent_type: null as (1 | 2) | null,
        is_monitored: false,
        any_new_data_activate: false,
        ppg_ecg_new_data_activate: false,
        auto_deactivate_after: null as string | null,
        status_change_email: false,
        clinic_uuid: null as string | null,
        physician_uuid: null as string | null,
      }),
    );

    async function save() {
      const { monitoring_type } = fields;
      assert(monitoring_type != null, "Monitoring type is required");

      await apiPostMonitoringSettingsMonitoring({
        body: {
          ...fields,
          monitoring_type,
          new_patient_status: fields.new_patient_status ?? undefined,
          consent_type: fields.consent_type ?? undefined,
        },
      });
      onSaved();
      onClose();
    }

    return (
      <Dialog open onOpenChange={(open) => !open && onClose()}>
        <DialogHeader>
          <Trans>Add new monitoring settings</Trans>
        </DialogHeader>
        <Form className="flex flex-col min-h-0 flex-1" onSubmit={() => save()}>
          <DialogBody>
            <MonitoringSettingsForm
              fields={fields}
              admin={admin}
              mode="create"
            />
          </DialogBody>

          <DialogFooter>
            <Button type="button" onClick={onClose}>
              <Trans>Cancel</Trans>
            </Button>
            <Button variant="primary" type="submit">
              <Trans>Add monitoring settings</Trans>
            </Button>
          </DialogFooter>
        </Form>
      </Dialog>
    );
  },
);
